/**
 * DashboardPage — /dashboard (US-17-GR-27)
 *
 * Owner landing page: a single screen of headline KPI tiles with
 * vs-prior-period deltas and sparklines so "am I making money, are users
 * sticking, what is AI costing me, is anything on fire" is answerable in
 * ten seconds. This is now the default route (`/` redirects here — App.tsx).
 *
 * Sources (four independent loads so one failing source degrades only its
 * own tiles, never the whole page):
 *   1. GET /api/admin/reports/overview?from=&to= (adminReportsService,
 *      new for this story) — `{ tierDistribution, newSignups, funnel, users,
 *      errors }`. Every top-level section is independently nullable even on
 *      a 200 (server-side partial failure, reported per-area in `errors`);
 *      a whole-request failure (e.g. 404 — endpoint not deployed yet)
 *      degrades all three overview tiles to an error state, not a blank
 *      page. tierDistribution/users carry no prior-period value (no delta
 *      shown for MRR or active-users tiles); newSignups and funnel each
 *      carry `{ current, prior }` for the vs-prior-period badge.
 *   2. GET /api/admin/orchestrator/cost (orchestratorClient.getCost,
 *      groupBy=time) — AI spend today + window-to-date, daily sparkline.
 *   3. GET /api/admin/orchestrator/jobs (orchestratorClient.getJobs) — job
 *      success rate computed from the fetched page's status counts (labelled
 *      honestly when that page is a sample of a larger total).
 *   4. GET /api/admin/orchestrator/queues (orchestratorClient.getQueues) —
 *      live queue + DLQ depth snapshot, not window-scoped.
 *
 * 5. GET /api/v1/admin/producers (adminProducersService, US-048-08) - the
 *      "Feed producers" tile shows the worst status across producers. Not
 *      windowed; its failure degrades only this tile.
 *
 * Window selector (7/30/90d, default 30d, shared localStorage key with
 * AnalyticsPage/CostAnalysisPage) drives the overview from/to range and the
 * cost/jobs window param. Queues is a live snapshot and is not windowed.
 */

import { useCallback, useEffect, useMemo, useState, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, AlertCircle, ArrowRight } from 'lucide-react';
import {
  TimeWindowSelector,
  loadStoredWindow,
  saveStoredWindow,
  type TimeWindow,
} from '../components/TimeWindowSelector';
import { presetRange } from '../components/reports/DateRangePicker';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Sparkline } from '../components/Sparkline';
import { AbbrLabel } from '../components/AbbrLabel';
import {
  getOverviewReport,
  type OverviewReportResponse,
  type OverviewTierDistribution,
  type OverviewNewSignups,
  type OverviewFunnel,
  type OverviewUsers,
  type ReportAreaError,
} from '../services/adminReportsService';
import {
  getCost,
  getJobs,
  getQueues,
  type CostResponse,
  type PagedResult,
  type AgentJobSummaryDto,
  type QueuesResponse,
} from '../api/orchestratorClient';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';
import { getProducers, type ProducerListResponse } from '../services/adminProducersService';
import {
  PRODUCER_STATUS_CLASS,
  PRODUCER_STATUS_LABEL,
  worstProducerStatus,
} from '../utils/producerStatus';
import {
  countActiveSubscribers,
  countNeverSubscribed,
  splitTierRows,
} from '../utils/tierDistribution';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtUsd0(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

function fmtUsd2(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v);
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// vs-prior-period delta badge — hidden when prior data doesn't exist
// ---------------------------------------------------------------------------

function DeltaBadge({ current, prior }: { current: number; prior: number | null | undefined }) {
  if (prior === null || prior === undefined) return null;

  const diff = current - prior;

  if (prior === 0) {
    if (diff === 0) return <span className="text-xs text-zinc-500">flat vs prior period</span>;
    const up = diff > 0;
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}
        aria-label={`${up ? 'up' : 'down'} vs prior period (prior period was zero)`}
      >
        {up ? '▲' : '▼'} new vs prior period
      </span>
    );
  }

  const pct = (diff / Math.abs(prior)) * 100;
  const isFlat = Math.abs(pct) < 0.05;
  const isUp = pct > 0;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        isFlat ? 'text-zinc-500' : isUp ? 'text-emerald-400' : 'text-red-400'
      }`}
      aria-label={isFlat ? 'flat vs prior period' : `${isUp ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)} percent vs prior period`}
    >
      {isFlat ? '' : isUp ? '▲' : '▼'} {isFlat ? 'flat' : `${Math.abs(pct).toFixed(1)}%`} vs prior
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tile shell — shared loading / error / deep-link chrome for every KPI tile
// ---------------------------------------------------------------------------

interface TileCardProps {
  title: string;
  /** Optional visual override for `title` (e.g. wraps an abbreviation in AbbrLabel).
   * `title` itself stays a plain string — it also drives aria-labels below. */
  titleNode?: React.ReactNode;
  to: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  children: React.ReactNode;
}

function TileCard({ title, titleNode, to, loading, error, onRetry, children }: TileCardProps) {
  const header = (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{titleNode ?? title}</h3>
      {!loading && !error && (
        <ArrowRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" aria-hidden="true" />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 bg-zinc-900 rounded-xl border border-zinc-800 min-h-[48px]">
        {header}
        <div role="status" aria-label={`Loading ${title}`} className="flex items-center gap-2 text-zinc-500 text-xs">
          <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 bg-zinc-900 rounded-xl border border-red-900/40 min-h-[48px]">
        {header}
        <div role="alert" className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 text-red-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
            <span>Failed to load {title.toLowerCase()}</span>
          </div>
          <p className="text-[11px] text-zinc-500 break-words">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="self-start px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors min-h-[48px]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      aria-label={`${title} — view details`}
      className="group block p-5 bg-zinc-900 rounded-xl border border-zinc-800 hover:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-colors min-h-[48px]"
    >
      {header}
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Overview per-area degradation — a section can be null while the overview
// request itself succeeded (server-side partial failure); `errors` says why.
// Area-name matching is a best-effort heuristic against the free-form
// ReportAreaError.area string, since the contract doesn't enumerate values.
// ---------------------------------------------------------------------------

function findAreaError(errors: ReportAreaError[] | undefined, pattern: RegExp): string | null {
  return errors?.find((e) => pattern.test(e.area))?.error ?? null;
}

function SectionErrorNote({ message }: { message: string }) {
  return (
    <div role="alert" className="flex items-start gap-1.5 text-amber-400 text-xs">
      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile content — overview-sourced tiles (MRR, signups, active users)
// ---------------------------------------------------------------------------

function MrrTileContent({
  tierDistribution,
  sectionError,
}: {
  tierDistribution: OverviewTierDistribution | null;
  sectionError: string | null;
}) {
  if (!tierDistribution) {
    return sectionError ? (
      <SectionErrorNote message={sectionError} />
    ) : (
      <p className="text-sm text-zinc-500">No revenue data for this period.</p>
    );
  }
  // This used to total EVERY bucket, including the synthetic `unknown` one, which
  // holds profiles that have never had a subscription at all. On DEV that rendered
  // "51,108 active subscribers" against 2 rows in ProfileDB.UserSubscription — and
  // beside an Active Users tile reading 14,201, so the dashboard claimed more
  // subscribers than users. See src/utils/tierDistribution.ts for why the bucket
  // exists and must still be shown, just never counted as a subscriber.
  const activeSubs = countActiveSubscribers(tierDistribution.tiers);
  const neverSubscribed = countNeverSubscribed(tierDistribution.tiers);
  const topTiers = splitTierRows(tierDistribution.tiers).subscribed.filter((t) => t.count > 0);
  return (
    <div>
      <p className="text-2xl font-semibold text-zinc-100">
        {fmtUsd0(tierDistribution.estimatedMrrUsd)}
        <span className="text-xs text-zinc-500 font-normal ml-1.5">
          estimated <AbbrLabel term="MRR" />
        </span>
      </p>
      {/* DEC-2026-08-07 C3: this figure omits provider-marketplace seat revenue entirely, so it is
          not platform-wide MRR. The scope label stands until code parity (C2) makes it canonical. */}
      <p className="text-xs text-amber-400/80 mt-1">consumer subscriptions only</p>
      <p className="text-xs text-zinc-500 mt-3">{activeSubs.toLocaleString()} active subscribers</p>
      {topTiers.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
          {topTiers.map((t) => (
            <span key={t.tier} className="text-[11px] text-zinc-400 capitalize">
              {t.tier}: {t.count.toLocaleString()}
            </span>
          ))}
        </div>
      )}
      {neverSubscribed > 0 && (
        // Bug 15182: this bucket is TaskManagerDB.dbo.UserProfile's own row count, which the
        // bug's own SQL measurement showed is 92.7% drifted from ProfileDB reality — duplicates,
        // nulls, and nightly E2E test accounts. State that plainly so it never reads as a
        // trustworthy user total.
        <p className="text-[10px] text-amber-400/80 mt-1">
          {neverSubscribed.toLocaleString()} profiles have no subscription on record — counted here as
          context, never as subscribers. This is TaskManager's own profile mirror, a known
          data-quality gap (heavily drifted with duplicate/test data) — not a reliable user count;
          see /users for the trustworthy figure.
        </p>
      )}
    </div>
  );
}

function SignupsTileContent({
  newSignups,
  funnel,
  sectionError,
}: {
  newSignups: OverviewNewSignups | null;
  funnel: OverviewFunnel | null;
  sectionError: string | null;
}) {
  if (!newSignups && !funnel) {
    return sectionError ? (
      <SectionErrorNote message={sectionError} />
    ) : (
      <p className="text-sm text-zinc-500">No signup data for this period.</p>
    );
  }
  const series =
    newSignups && newSignups.dailySeries.length > 0
      ? [{ values: newSignups.dailySeries.map((p) => p.count), color: '#8b5cf6', label: 'Daily signups' }]
      : null;
  return (
    <div>
      {newSignups ? (
        <>
          <p className="text-2xl font-semibold text-zinc-100">
            {newSignups.current.toLocaleString()}
            <span className="text-xs text-zinc-500 font-normal ml-1.5">new signups</span>
          </p>
          <div className="mt-1">
            <DeltaBadge current={newSignups.current} prior={newSignups.prior} />
          </div>
        </>
      ) : (
        <p className="text-sm text-zinc-500">
          Signup counts unavailable{sectionError ? ` — ${sectionError}` : ''}.
        </p>
      )}
      {funnel ? (
        funnel.current.conversionHasData === false ? (
          <p className="text-xs text-zinc-500 mt-3">
            Onboarding conversion: <span className="text-zinc-300">—</span>
            <span className="text-zinc-600"> (no submissions started in this window)</span>
          </p>
        ) : (
          <p className="text-xs text-zinc-500 mt-3">
            Onboarding conversion:{' '}
            <span className="text-zinc-300">{funnel.current.conversionPct.toFixed(1)}%</span>
            <span className="text-zinc-600">
              {' '}
              {funnel.current.submissionStarted !== undefined &&
              funnel.current.submissionStarted !== null
                ? `(${funnel.current.submissionStarted.toLocaleString()} → ${(funnel.current.submissionConverted ?? 0).toLocaleString()})`
                : `(${funnel.current.starts.toLocaleString()} → ${funnel.current.submissions.toLocaleString()})`}
            </span>
            {funnel.prior.conversionHasData !== false && (
              <span className="ml-1.5">
                <DeltaBadge current={funnel.current.conversionPct} prior={funnel.prior.conversionPct} />
              </span>
            )}
          </p>
        )
      ) : (
        <p className="text-[11px] text-zinc-600 mt-3">Onboarding conversion unavailable.</p>
      )}
      {series && (
        <div className="mt-3">
          <Sparkline series={series} width={220} height={40} />
        </div>
      )}
    </div>
  );
}

function ActiveUsersTileContent({
  users,
  sectionError,
}: {
  users: OverviewUsers | null;
  sectionError: string | null;
}) {
  if (!users) {
    return sectionError ? (
      <SectionErrorNote message={sectionError} />
    ) : (
      <p className="text-sm text-zinc-500">No user data for this period.</p>
    );
  }
  const pctActive = users.totalUsers > 0 ? (users.activeUsersLast30Days / users.totalUsers) * 100 : 0;
  return (
    <div>
      <p className="text-2xl font-semibold text-zinc-100">
        {users.activeUsersLast30Days.toLocaleString()}
        <span className="text-xs text-zinc-500 font-normal ml-1.5">
          / {users.totalUsers.toLocaleString()} total
        </span>
      </p>
      {/* Bug 15182: the 14,201-style total here is COUNT(DISTINCT Board.ProfileId) — profiles
          with a board — not every account. Label it at the point of display, not just in the
          footnote below. */}
      <p className="text-xs text-zinc-500 mt-1">profiles with a board, not all users</p>
      <p className="text-xs text-zinc-500 mt-3">{pctActive.toFixed(1)}% active in the last 30 days</p>
      {/* US-17-GR-35: "active" = emitted a UsageEvent; ChellessAdmin/IbaApp/
          ProviderConsole/CustomerStatus/Mobile emit none, so their usage is
          unmeasured, not zero. Plain text (not a focusable badge) because this
          tile's whole body is already the TileCard <Link> — nesting another
          focusable control here would break tab order / hit-testing. */}
      <p className="text-[10px] text-zinc-600 mt-1">
        Counts UsageEvent activity only — provider, agent, and mobile surfaces emit none, so their usage is
        unmeasured here, not zero.
      </p>
      {/* The denominator is COUNT(DISTINCT Board.ProfileId), not a user count. Three
          different populations are called "users" across this console — 8,718 on
          /users (Profile.API, the identity source of truth), 14,201 here, and 51,108
          in the Revenue tile's tier breakdown. Each is correct for its own rail and
          none said so, which is how a 5.9x spread went unnoticed. Until they are
          reconciled (work item 15200), every one of them states its population. */}
      <p className="text-[10px] text-zinc-600 mt-1">
        Denominator is board owners, not all accounts — see /users for the identity roster.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile content — AI spend, job success rate, queues
// ---------------------------------------------------------------------------

function AiSpendTileContent({ data }: { data: CostResponse }) {
  const rows = [...data.rows].sort((a, b) => a.key.localeCompare(b.key));
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayRow = rows.find((r) => r.key.slice(0, 10) === todayIso);
  const series =
    rows.length > 0
      ? [{ values: rows.map((r) => r.estimatedUsd), color: '#f59e0b', label: 'Daily spend' }]
      : null;
  return (
    <div>
      <p className="text-2xl font-semibold text-zinc-100">
        {fmtUsd2(todayRow?.estimatedUsd ?? 0)}
        <span className="text-xs text-zinc-500 font-normal ml-1.5">today</span>
      </p>
      <p className="text-xs text-zinc-400 mt-1">
        {fmtUsd2(data.totalEstimatedUsd)}
        <span className="text-zinc-500"> window-to-date</span>
      </p>
      {series && (
        <div className="mt-3">
          <Sparkline series={series} width={220} height={40} />
        </div>
      )}
    </div>
  );
}

function JobsTileContent({ jobs }: { jobs: PagedResult<AgentJobSummaryDto> }) {
  // Work item 17433: this tile used to compute the rate from the 100 rows it happened to be sent.
  // DEV holds 1,137 jobs in a 30-day window, 88% of them Failed, so it could read anything between
  // 0% and 100% depending on which 100 came back - while rendering as a headline percentage. The
  // rate now comes from a server-side GROUP BY over the whole window.
  //
  // The client-side fallback stays for an API build that predates successRate, and says so in its
  // own footnote rather than passing a sample off as the window.
  const counts = jobs.statusCounts;
  const completed = counts?.Completed ?? jobs.items.filter((j) => j.status === 'Completed').length;
  const failed = counts?.Failed ?? jobs.items.filter((j) => j.status === 'Failed').length;

  const serverRate = jobs.successRate;
  const usingServerRate = serverRate !== undefined;

  const terminal = completed + failed;
  const successRate = usingServerRate
    ? (serverRate === null ? null : serverRate * 100)
    : (terminal > 0 ? (completed / terminal) * 100 : null);

  const sampleNote = usingServerRate
    ? `based on all ${jobs.total.toLocaleString()} jobs in window`
    : jobs.items.length < jobs.total
      ? `based on the latest ${jobs.items.length.toLocaleString()} of ${jobs.total.toLocaleString()} jobs`
      : `based on all ${jobs.total.toLocaleString()} jobs in window`;
  return (
    <div>
      <p className="text-2xl font-semibold text-zinc-100">
        {successRate === null ? '—' : `${successRate.toFixed(1)}%`}
        <span className="text-xs text-zinc-500 font-normal ml-1.5">success rate</span>
      </p>
      <p className="text-xs text-red-400 mt-1">{failed.toLocaleString()} failed jobs</p>
      <p className="text-[11px] text-zinc-600 mt-2">{sampleNote}</p>
    </div>
  );
}

function QueuesTileContent({ queues }: { queues: QueuesResponse }) {
  const dlqTotal = queues.tasks.dlqDepth + queues.events.dlqDepth;
  const onFire = dlqTotal > 0;
  return (
    <div>
      <p className={`text-2xl font-semibold ${onFire ? 'text-red-400' : 'text-zinc-100'}`}>
        {dlqTotal.toLocaleString()}
        <span className="text-xs text-zinc-500 font-normal ml-1.5">
          <AbbrLabel term="DLQ" /> messages
        </span>
      </p>
      <div className="flex gap-4 mt-2 text-xs text-zinc-400">
        <span>Tasks queue: {queues.tasks.depth.toLocaleString()}</span>
        <span>Events queue: {queues.events.depth.toLocaleString()}</span>
      </div>
      {onFire && (
        <p className="text-[11px] text-red-400 mt-2 font-medium">Dead letters present — needs attention</p>
      )}
    </div>
  );
}

function FeedProducersTileContent({ producers }: { producers: ProducerListResponse['producers'] }) {
  const worst = worstProducerStatus(producers.map((p) => p.status));
  if (!worst) {
    return <p className="text-xs text-zinc-500">No producers reported.</p>;
  }
  const needAttention = producers.filter((p) => p.status === 'failing' || p.status === 'overdue').length;
  return (
    <div>
      <span
        data-testid="feed-producers-worst-status"
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold border ${PRODUCER_STATUS_CLASS[worst]}`}
      >
        {PRODUCER_STATUS_LABEL[worst]}
      </span>
      <p className="text-xs text-zinc-400 mt-2">
        {needAttention > 0
          ? `${needAttention} of ${producers.length} producers need attention`
          : `${producers.length} producers, none failing`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Load-state helper — one per independent data source
// ---------------------------------------------------------------------------

interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const initialLoadState = <T,>(): LoadState<T> => ({ data: null, loading: true, error: null });

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  // `range` is intentionally pinned to the moment the window changes — recomputing
  // `now` every render would give loadOverview a new [from, to] each time and
  // re-fetch forever (presetRange defaults `now` to `new Date()`).
  const range = useMemo(() => presetRange(timeWindow), [timeWindow]);

  const [overview, setOverview] = useState<LoadState<OverviewReportResponse>>(initialLoadState);
  const [cost, setCost] = useState<LoadState<CostResponse>>(initialLoadState);
  const [jobs, setJobs] = useState<LoadState<PagedResult<AgentJobSummaryDto>>>(initialLoadState);
  const [queues, setQueues] = useState<LoadState<QueuesResponse>>(initialLoadState);
  const [producers, setProducers] = useState<LoadState<ProducerListResponse>>(initialLoadState);

  const beginOverviewLoad = useLatestRequest();
  const beginCostLoad = useLatestRequest();
  const beginJobsLoad = useLatestRequest();

  const loadOverview = useCallback(async () => {
    const isCurrent = beginOverviewLoad();
    setOverview((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getOverviewReport(range.from, range.to, COLDSTART_RETRY_CONFIG);
      if (!isCurrent()) return;
      setOverview({ data, loading: false, error: null });
    } catch (err) {
      if (!isCurrent()) return;
      setOverview({ data: null, loading: false, error: messageOf(err, 'Failed to load overview') });
    }
  }, [beginOverviewLoad, range.from, range.to]);

  const loadCost = useCallback(async () => {
    const isCurrent = beginCostLoad();
    setCost((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getCost({ window: timeWindow, groupBy: 'time' }, COLDSTART_RETRY_CONFIG);
      if (!isCurrent()) return;
      setCost({ data, loading: false, error: null });
    } catch (err) {
      if (!isCurrent()) return;
      setCost({ data: null, loading: false, error: messageOf(err, 'Failed to load AI spend') });
    }
  }, [beginCostLoad, timeWindow]);

  const loadJobs = useCallback(async () => {
    const isCurrent = beginJobsLoad();
    setJobs((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJobs(
        { window: timeWindow, pageSize: 100, sortBy: 'startedAt', sortDesc: true },
        COLDSTART_RETRY_CONFIG,
      );
      if (!isCurrent()) return;
      setJobs({ data, loading: false, error: null });
    } catch (err) {
      if (!isCurrent()) return;
      setJobs({ data: null, loading: false, error: messageOf(err, 'Failed to load job stats') });
    }
  }, [beginJobsLoad, timeWindow]);

  const loadQueues = useCallback(async () => {
    setQueues((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getQueues(COLDSTART_RETRY_CONFIG);
      setQueues({ data, loading: false, error: null });
    } catch (err) {
      setQueues({ data: null, loading: false, error: messageOf(err, 'Failed to load queue depths') });
    }
  }, []);

  const loadProducers = useCallback(async () => {
    setProducers((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getProducers();
      setProducers({ data, loading: false, error: null });
    } catch (err) {
      setProducers({ data: null, loading: false, error: messageOf(err, 'Failed to load feed producers') });
    }
  }, []);

  useEffect(() => {
    startTransition(() => { void loadOverview(); });
  }, [loadOverview]);

  useEffect(() => {
    startTransition(() => { void loadCost(); });
  }, [loadCost]);

  useEffect(() => {
    startTransition(() => { void loadJobs(); });
  }, [loadJobs]);

  useEffect(() => {
    startTransition(() => { void loadQueues(); });
  }, [loadQueues]);

  useEffect(() => {
    startTransition(() => { void loadProducers(); });
  }, [loadProducers]);

  const handleWindowChange = (w: TimeWindow) => {
    setTimeWindow(w);
    saveStoredWindow(w);
  };

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Dashboard</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Headline KPIs vs the prior {timeWindow}-day period — click a tile for the full report
          </p>
        </div>
        <TimeWindowSelector value={timeWindow} onChange={handleWindowChange} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <TileCard
          title="Revenue"
          to="/reports/billing"
          loading={overview.loading}
          error={overview.error}
          onRetry={() => void loadOverview()}
        >
          <MrrTileContent
            tierDistribution={overview.data?.tierDistribution ?? null}
            sectionError={findAreaError(overview.data?.errors, /billing|revenue|tier|mrr/i)}
          />
        </TileCard>

        <TileCard
          title="Signups & Onboarding"
          to="/reports/onboarding"
          loading={overview.loading}
          error={overview.error}
          onRetry={() => void loadOverview()}
        >
          <SignupsTileContent
            newSignups={overview.data?.newSignups ?? null}
            funnel={overview.data?.funnel ?? null}
            sectionError={findAreaError(overview.data?.errors, /signup|onboarding|funnel/i)}
          />
        </TileCard>

        <TileCard
          title="Active Users"
          to="/analytics"
          loading={overview.loading}
          error={overview.error}
          onRetry={() => void loadOverview()}
        >
          <ActiveUsersTileContent
            users={overview.data?.users ?? null}
            sectionError={findAreaError(overview.data?.errors, /user/i)}
          />
        </TileCard>

        <TileCard
          title="AI Spend"
          titleNode={<><AbbrLabel term="AI" /> Spend</>}
          to="/cost"
          loading={cost.loading}
          error={cost.error}
          onRetry={() => void loadCost()}
        >
          {cost.data && <AiSpendTileContent data={cost.data} />}
        </TileCard>

        <TileCard
          title="Job Success Rate"
          to="/jobs"
          loading={jobs.loading}
          error={jobs.error}
          onRetry={() => void loadJobs()}
        >
          {jobs.data && <JobsTileContent jobs={jobs.data} />}
        </TileCard>

        <TileCard
          title="Queue Health"
          to="/queues"
          loading={queues.loading}
          error={queues.error}
          onRetry={() => void loadQueues()}
        >
          {queues.data && <QueuesTileContent queues={queues.data} />}
        </TileCard>

        <TileCard
          title="Feed producers"
          to="/feed/producers"
          loading={producers.loading}
          error={producers.error}
          onRetry={() => void loadProducers()}
        >
          {producers.data && <FeedProducersTileContent producers={producers.data.producers} />}
        </TileCard>
      </div>
    </main>
  );
}

export default DashboardPage;
